import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

interface SubscriptionContextType {
  isActive: boolean;
  isLoading: boolean;
  error: string | null;
  checkSubscriptionStatus: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkSubscriptionStatus = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Force-refresh the token to get latest custom claims set by the webhook
      const tokenResult = await user.getIdTokenResult(true);
      const claims = tokenResult.claims as any;
      setIsActive(claims?.subscription?.status === "active");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsActive(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    } else {
      setIsActive(false);
    }
  }, [user?.uid]);

  const value = {
    isActive,
    isLoading,
    error,
    checkSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
